import { apiClient } from "./client";
import type {
  Desideratum,
  DesiderataKind,
  DesiderataWindow,
  DesiderataWindowStatus,
} from "./types";

// ----------------------------------------------------------------------------
// Windows (coord)
// ----------------------------------------------------------------------------
export async function listWindows(params?: {
  organization_id?: string;
  status?: DesiderataWindowStatus;
}): Promise<DesiderataWindow[]> {
  const { data } = await apiClient.get<DesiderataWindow[]>(
    "/api/v1/desiderata-windows",
    { params },
  );
  return data;
}

export type CreateWindowPayload = {
  organization_id: string;
  year: number;
  month: number;
  opens_at: string; // ISO
  closes_at: string;
  quota_points?: number;
  description?: string | null;
};

export async function createWindow(p: CreateWindowPayload): Promise<DesiderataWindow> {
  const { data } = await apiClient.post<DesiderataWindow>("/api/v1/desiderata-windows", p);
  return data;
}

export async function updateWindow(
  id: string,
  p: Partial<{
    opens_at: string;
    closes_at: string;
    status: DesiderataWindowStatus;
    quota_points: number;
    description: string | null;
  }>,
): Promise<DesiderataWindow> {
  const { data } = await apiClient.patch<DesiderataWindow>(
    `/api/v1/desiderata-windows/${id}`,
    p,
  );
  return data;
}

export async function deleteWindow(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/desiderata-windows/${id}`);
}

export async function listDesiderataForWindow(
  windowId: string,
  practitionerId?: string,
): Promise<Desideratum[]> {
  const { data } = await apiClient.get<Desideratum[]>(
    `/api/v1/desiderata-windows/${windowId}/desiderata`,
    { params: practitionerId ? { practitioner_id: practitionerId } : undefined },
  );
  return data;
}

// ----------------------------------------------------------------------------
// Self (practitioner)
// ----------------------------------------------------------------------------
export async function listMyOpenWindows(): Promise<DesiderataWindow[]> {
  const { data } = await apiClient.get<DesiderataWindow[]>(
    "/api/v1/me/desiderata-windows",
  );
  return data;
}

export async function listMyDesiderata(windowId?: string): Promise<Desideratum[]> {
  const { data } = await apiClient.get<Desideratum[]>("/api/v1/me/desiderata", {
    params: windowId ? { window_id: windowId } : undefined,
  });
  return data;
}

export type CreateMyDesideratumPayload = {
  date: string;
  slot_template_id?: string | null;
  kind: DesiderataKind;
  weight: number;
  comment?: string | null;
};

export async function createMyDesideratum(
  windowId: string,
  p: CreateMyDesideratumPayload,
): Promise<Desideratum> {
  const { data } = await apiClient.post<Desideratum>(
    `/api/v1/me/desiderata-windows/${windowId}/desiderata`,
    p,
  );
  return data;
}

export async function deleteMyDesideratum(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/me/desiderata/${id}`);
}

export type BulkCell = {
  date: string;
  slot_template_id?: string | null;
};

export type BulkApplyPayload = {
  cells: BulkCell[];
  kind: DesiderataKind;
  weight?: number;
  comment?: string | null;
  replace_existing?: boolean;
};

export type BulkResult = {
  created: number;
  replaced: number;
  skipped_duplicate: number;
  skipped_quota: number;
  errors: string[];
};

export async function bulkApplyMyDesiderata(
  windowId: string,
  payload: BulkApplyPayload,
): Promise<BulkResult> {
  const { data } = await apiClient.post<BulkResult>(
    `/api/v1/me/desiderata-windows/${windowId}/desiderata/bulk`,
    payload,
  );
  return data;
}
